import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../services/firebase';
import PublicNavBar from './PublicNavBar';
import { useModal } from '../contexts/ModalContext';
import { Helmet } from 'react-helmet-async';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const { showAlert } = useModal();

  const handleSubmit = async () => {
    console.log(`${isSignUp ? 'Sign up' : 'Login'} attempted with:`, email, password);
    
    if (!email || !password) {
      await showAlert('Please enter both email and password', 'Missing Fields');
      return;
    }
    
    if (password.length < 6) {
      await showAlert('Password must be at least 8 characters with 1 number and 1 capital', 'Weak Password');
      return;
    }
    
    try {
      if (isSignUp) {
        await signup(email, password);
        console.log('Sign up successful');
      } else {
        await login(email, password);
        console.log('Login successful');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error(`${isSignUp ? 'Sign up' : 'Login'} failed:`, error);
      await showAlert(`${isSignUp ? 'Sign up' : 'Login'} failed: ` + error.message, 'Error');
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign in successful:', result.user);
      navigate('/dashboard');
    } catch (error) {
      console.error('Google sign in failed:', error);
      await showAlert('Google sign in failed: ' + error.message, 'Error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-sand-100 to-sand-200">
      <Helmet>
        <title>Login | TeAIch Suite — AI Tools for Teachers</title>
        <meta name="description" content="Log in to TeAIch Suite to access AI marking and lesson planning tools. New to TeAIch? Sign up today." />
        <link rel="canonical" href="https://teaich.app/login" />
      </Helmet>
      <PublicNavBar />
      <div className="flex items-center justify-center py-6 sm:py-8 md:py-12 flex-grow px-2 sm:px-4">
        <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-4 sm:p-6 md:p-12 w-full max-w-2xl mx-2 sm:mx-4">
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-800">
              {isSignUp ? 'Create Your Account' : 'Login to Your Account'}
            </h2>
            <p className="text-ink-600 mt-2 sm:mt-3 text-sm sm:text-base md:text-lg">
              {isSignUp ? 'Join TeAIch to start marking student work!' : 'Welcome back! Please log in to continue.'}
            </p>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="mb-4 sm:mb-6">
              <label className="block text-ink-700 text-xs sm:text-sm font-bold mb-2 sm:mb-3">EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your email"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base md:text-lg"
              />
            </div>
            
            <div className="mb-6 sm:mb-8">
              <label className="block text-ink-700 text-xs sm:text-sm font-bold mb-2 sm:mb-3">PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base md:text-lg"
              />
              {isSignUp && (
                <p className="text-xs sm:text-sm text-ink-500 mt-2">Password must be at least 8 characters, Minimum 1 Uppercase and Minimum 1 number</p>
              )}
            </div>
            
            <button
              onClick={handleSubmit}
              className="w-full bg-coral-300 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg hover:bg-coral-500 transition duration-200 text-base sm:text-lg font-semibold"
            >
              {isSignUp ? 'Sign Up' : 'Login'}
            </button>
          </div>
          
          <div className="mt-4 sm:mt-6 md:mt-8 max-w-md mx-auto">
            <div className="flex items-center my-4 sm:my-6">
              <div className="flex-grow border-t border-sand-300"></div>
              <span className="mx-4 text-ink-500 text-sm">or</span>
              <div className="flex-grow border-t border-sand-300"></div>
            </div>
            
            <button
              onClick={handleGoogleSignIn}
              className="w-full bg-white border-2 border-sand-300 text-ink-700 py-2 sm:py-3 px-4 sm:px-6 rounded-lg hover:bg-sand-50 transition duration-200 text-base sm:text-lg font-semibold flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>
          
          <p className="text-center mt-4 sm:mt-6 text-ink-600 text-sm sm:text-base md:text-lg">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <span 
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-coral-600 cursor-pointer hover:underline font-semibold"
            >
              {isSignUp ? 'Login here' : 'Sign up here'}
            </span>
          </p>
        </div>
      </div>

      <footer className="bg-sand-100 border-t-4 border-coral-300 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Mobile: stacked, Desktop: single row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            
            {/* Logo */}
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 w-auto" />
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a href="YOUR_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/teaich_app/" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="YOUR_TIKTOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.56V6.81a4.85 4.85 0 01-1.07-.12z"/>
                </svg>
              </a>
              <a href="YOUR_LINKEDIN_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>

            {/* Nav Links */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/feedback')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Feedback
              </button>
              <button onClick={() => navigate('/legal/privacy-policy')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Privacy Policy
              </button>
              <button onClick={() => navigate('/legal/terms-of-service')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Terms of Service
              </button>
              <button onClick={() => navigate('/legal/data-retention')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Data Retention
              </button>
            </div>

          </div>
        </div>
      </footer>
    </div>  
  );        
};

export default LoginPage;